import { useCallback, useContext, useEffect, useState } from 'react';
import { ChangePathContext } from '../App';

export const useUnsavedChanges = (hasChanges = false, onDirectClose) => {
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const isDirty = Boolean(hasChanges);
  const context = useContext(ChangePathContext);
  const setSaveChanges = context?.setSaveChanges;

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        // If a nested modal/dialog (like image uploader / crop popup) is open on top, let it handle Escape
        const activeModals = document.querySelectorAll(
          '.MuiModal-root:not([aria-hidden="true"]), .MuiDialog-root:not([aria-hidden="true"])',
        );
        if (activeModals && activeModals.length > 1) {
          return;
        }

        e.preventDefault();
        e.stopPropagation();
        if (isDirty) {
          setShowUnsavedModal(true);
        } else if (onDirectClose) {
          onDirectClose();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isDirty, onDirectClose]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty]);

  useEffect(() => {
    if (setSaveChanges) {
      setSaveChanges((prev) => ({
        ...prev,
        isUnsaveChanges: isDirty,
      }));
    }
    return () => {
      if (setSaveChanges) {
        setSaveChanges((prev) => ({
          ...prev,
          isUnsaveChanges: false,
        }));
      }
    };
  }, [isDirty, setSaveChanges]);

  const handleRequestClose = useCallback(
    (customClose) => {
      if (isDirty) {
        setShowUnsavedModal(true);
      } else {
        const closeFn = typeof customClose === 'function' ? customClose : onDirectClose;
        if (typeof closeFn === 'function') closeFn();
      }
    },
    [isDirty, onDirectClose],
  );

  const handleConfirmLeave = useCallback(
    (customLeave) => {
      setShowUnsavedModal(false);
      if (setSaveChanges) {
        setSaveChanges((prev) => ({
          ...prev,
          isUnsaveChanges: false,
          showChangesPopup: false,
        }));
      }
      const leaveFn = typeof customLeave === 'function' ? customLeave : onDirectClose;
      if (typeof leaveFn === 'function') leaveFn();
    },
    [onDirectClose, setSaveChanges],
  );

  const handleStay = useCallback(() => {
    setShowUnsavedModal(false);
  }, []);

  return {
    showUnsavedModal,
    setShowUnsavedModal,
    handleRequestClose,
    handleConfirmLeave,
    handleStay,
  };
};

export default useUnsavedChanges;
